
import {  useState } from 'react';
import {
  App,
  AppIcon,
  ApplicationCard,
  ApplicationContainer,
  TabContainer,
  TabButton,
  DriveWarning,
  WarningIcon,
} from './styles';

const getIconText = (productName: any) => {
  return productName?.split(' ').reduce((ac: string, word: string) => {
    if (productName.split(' ').length === 1) ac = productName.substr(0, 3);
    else {
      if (productName.length <= 3) ac = word;
      else ac += word.charAt(0);
    }
    return ac.substring(0, 3).toUpperCase();
  }, '');
};

function canOpenDrive() {
  return navigator.userAgent.toLowerCase().indexOf('win') !== -1;
}

const getGroupedWorkflows = (app: any) => {
  if (!Array.isArray(app?.workflows)) return {};

  const filtered = app.workflows
    .filter(
      (wf: any) => !!wf?.workflowUrl && app?.unifyAppUrl !== wf.workflowUrl
    )
    .map((wf: any) => ({ ...wf, unifyAppUrl: wf.workflowUrl }));

  return filtered.reduce((acc: any, wf: any) => {
    const domain = wf.domain || '';
    if (!acc[domain]) acc[domain] = [];
    acc[domain].push(wf);
    return acc;
  }, {});
};

const WorkflowGroup = ({
  domain,
  workflowList,
  productName,
  launchUrl,
}: {
  domain: string;
  workflowList: any[];
  productName: string;
  launchUrl: (wf: any) => void;
}) => (
  <div>
    <div
      style={{
        margin: '20px 10px 20px 10px',
        fontSize: '16px',
        fontWeight: '600',
      }}
    >
      {productName} - {domain}
    </div>
    <ApplicationContainer>
      {workflowList.map((wf, index) => (
        <ApplicationCard
          key={wf.workflowId || index}
          onClick={() =>
            launchUrl({
              id: wf?.workflowId,
              name: wf?.workflowName,
              url: wf?.workflowUrl,
            })
          }
        >
          <App>
            <AppIcon>
              <div
                style={{
                  fontFamily: 'Montserrat,Roboto,  sans-serif',
                  display: 'flex',
                  padding: '0.16rem',
                  alignItems: 'flex-start',
                  backgroundColor: 'rgb(1, 48, 71)',
                  borderRadius: '2px',
                  boxSizing: 'border-box',
                  color: 'rgb(255, 255, 255)',
                  gridArea: '1 / 1',
                  flexFlow: 'row-reverse wrap-reverse',
                  fontSize: '18px',
                  fontWeight: '400',
                  height: '48px',
                  letterSpacing: '-0.06em',
                  lineHeight: '18px',
                  overflowWrap: 'anywhere',
                  overflow: 'hidden',
                  textAlign: 'end',
                  width: '48px',
                }}
              >
                {getIconText(wf?.workflowName)}
              </div>
            </AppIcon>
            <div>{wf?.workflowName || ''}</div>
          </App>
        </ApplicationCard>
      ))}
    </ApplicationContainer>
  </div>
);

export const Tabs = ({ sortedApplications, launchUrl }: any) => {
  const [activeTab, setActiveTab] = useState('applications');

  // Define your panels here
  const renderContent = () => {
    switch (activeTab) {
      case 'applications':
        return (
          <ApplicationContainer>
            {sortedApplications?.map((app: any, appIndex: number) => {
              const appId = app?.appId || app?.id;
              return (
                <ApplicationCard
                  key={`app-card-${appIndex}-${appId}`}
                  onClick={() =>
                    launchUrl({
                      id: appId,
                      name: app?.productName,
                      url: app?.unifyAppUrl,
                    })
                  }
                >
                  <App key={`app-${appIndex}-${appId}`}>
                    <AppIcon>
                      <div
                        style={{
                          fontFamily: 'Montserrat,Roboto,  sans-serif',
                          display: 'flex',
                          padding: '0.16rem',
                          alignItems: 'flex-start',
                          backgroundColor: 'rgb(1, 48, 71)',
                          borderRadius: '2px',
                          boxSizing: 'border-box',
                          color: 'rgb(255, 255, 255)',
                          gridArea: '1 / 1',
                          flexFlow: 'row-reverse wrap-reverse',
                          fontSize: '18px',
                          fontWeight: '400',
                          height: '48px',
                          letterSpacing: '-0.06em',
                          lineHeight: '18px',
                          overflowWrap: 'anywhere',
                          overflow: 'hidden',
                          textAlign: 'end',
                          width: '48px',
                        }}
                      >
                        {getIconText(app?.productName)}
                      </div>
                    </AppIcon>
                    <div>
                      <div>{app?.productName || ''}</div>
                      {appId === 'drive' && !canOpenDrive() && (
                        <DriveWarning>
                          <WarningIcon />
                          Windows required to launch Drive
                        </DriveWarning>
                      )}
                    </div>
                  </App>
                </ApplicationCard>
              );
            })}
          </ApplicationContainer>
        );
      case 'workflows':
        return (
          <div>
            {(() => {
              let hasWorkflows = false;
              const workflowElements = sortedApplications?.map(
                (app: any, appIndex: number) => {
                  const groupedWorkflows = getGroupedWorkflows(app);
                  const groupEntries = Object.entries(groupedWorkflows);

                  if (groupEntries.length > 0) {
                    hasWorkflows = true;
                  }

                  if (groupEntries.length === 0) return null;

                  return groupEntries.map(([domain, workflowList]) => (
                    <WorkflowGroup
                      key={`${app.appId || appIndex}-${domain}`}
                      domain={domain}
                      workflowList={workflowList as any[]}
                      productName={app.productName}
                      launchUrl={launchUrl}
                    />
                  ));
                }
              );

              if (!hasWorkflows) {
                return (
                  <div>
                    <h4 style={{ marginBottom: '10px' }}>
                      No Workflows Assigned
                    </h4>
                    <p>
                      Please contact your dealership administrator to provide
                      access to appropriate workflows.
                    </p>
                  </div>
                );
              }

              return workflowElements;
            })()}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* 1. The Tab Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px',
        }}
      >
        <TabContainer>
          <TabButton
            $isActive={activeTab === 'applications'}
            onClick={() => setActiveTab('applications')}
          >
            Applications
          </TabButton>
          <TabButton
            $isActive={activeTab === 'workflows'}
            onClick={() => setActiveTab('workflows')}
          >
            Workflows
          </TabButton>
        </TabContainer>
      </div>

      {/* 2. The Tab Panel (Content) */}
      <div style={{ padding: '20px' }}>{renderContent()}</div>
    </div>
  );
};
